import React from 'react';
import PropTypes from 'prop-types';
import { Typography, Box } from '@mui/material';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BaseCard from './BaseCard';
import { WINTER_GREEN } from '../../../dashboardColors';

// Custom tooltip component with "Count:" label in black
const CustomTooltip = ({ active, payload, label, t }) => {
  if (active && payload && payload.length) {
    const value = typeof payload[0].value === 'number' ? Math.round(payload[0].value) : payload[0].value;
    return (
      <div style={{
        backgroundColor: 'white',
        border: '1px solid #ccc',
        padding: '8px 12px',
        borderRadius: 4,
        fontSize: 12,
        fontWeight: 'bold'
      }}>
        <div style={{ marginBottom: 4 }}>{label}</div>
        <div style={{ color: '#000000' }}>
          {t('count')}: {value}
        </div>
      </div>
    );
  }
  return null;
};

const LeaksFoundCard = ({ data, t }) => {
  // Always show 4 years for consistent bar width
  // If we have data, use the most recent year as reference; otherwise use current year
  const allYearlyData = data?.yearly?.length > 0 ? data.yearly : [];
  const currentYear = new Date().getFullYear();
  const mostRecentYear = allYearlyData.length > 0
    ? Math.max(...allYearlyData.map(d => d.year))
    : currentYear;

  // Create array of 4 years ending with the most recent year
  const yearlyData = [];
  for (let i = 3; i >= 0; i--) {
    const year = mostRecentYear - i;
    const existingData = allYearlyData.find(d => d.year === year);
    yearlyData.push({
      year,
      leaks_fixed: existingData ? existingData.leaks_fixed : 0
    });
  }

  return (
    <BaseCard
      title={t('fixed_leaks_trend')}
      icon={<CheckCircleIcon />}
      className="leaks-found-card"
    >
      <Box display="flex" flexDirection="column" height="100%">
        <Box flex={1} minHeight={160} mt={1}>
          {allYearlyData.length > 0 ? (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={yearlyData} margin={{ top: 10, right: 10, left: 0, bottom: 10 }} barSize={70}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="year" tick={{ fontSize: 12, fontWeight: 'bold' }} />
                <YAxis tick={{ fontSize: 12, fontWeight: 'bold' }} tickFormatter={(value) => Math.round(value)} allowDecimals={false} />
                <Tooltip content={<CustomTooltip t={t} />} />
                <Bar dataKey="leaks_fixed" fill={WINTER_GREEN} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <Box height="100%" display="flex" alignItems="center" justifyContent="center">
              <Typography variant="body2" color="textSecondary" align="center">
                {t('no_yearly_data_available')}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </BaseCard>
  );
};

LeaksFoundCard.propTypes = {
  data: PropTypes.shape({
    total: PropTypes.number,
    fixed: PropTypes.number,
    yearly: PropTypes.arrayOf(PropTypes.shape({
      year: PropTypes.number,
      leaks_fixed: PropTypes.number
    }))
  }),
  t: PropTypes.func.isRequired
};

export default LeaksFoundCard;